/**
 * Optional platform capabilities whose backend is NOT deployed in this
 * environment. Pages for these render <FeatureUnavailable> instead of calling
 * an API that would fail (or worse, look like a real zero-result state).
 *
 * To enable a feature: deploy its backend, add it to mail-admin-service
 * NH_ENABLED_FEATURES (where applicable), then remove its entry here.
 */
export interface UnavailableFeature {
  title: string;
  summary: string;
  /** Backend component that must be deployed before this page can work. */
  backend: string;
  /**
   * On the dashboard roadmap (docs/dashboard-completion/ROADMAP.md, Phases A-D). Planned features keep
   * their navigation entry with a "Soon" badge; unplanned ones are hidden from navigation entirely
   * (their URL still shows the "not available" page).
   */
  planned?: boolean;
}

export const UNAVAILABLE_FEATURES = {
  'mail-tracking': { title: 'Mail Tracking', summary: 'Trace individual messages through delivery, filtering and quarantine.', backend: 'mail-trace-service' },
  'threat-dashboard': { title: 'Threat Dashboard', summary: 'Live threat intelligence across spam, malware and phishing.', backend: 'threat-intel-service' },
  'security-alerts': { title: 'Security Alerts', summary: 'Triage, assign and resolve security alerts raised by the mail pipeline.', backend: 'security-alert-service' },
  attachments: { title: 'Attachment Investigation', summary: 'Inspect and verdict attachments that passed through the mail pipeline.', backend: 'attachment-analysis-service' },
  'url-investigation': { title: 'URL Investigation', summary: 'Investigate links found in mail and their reputation.', backend: 'url-analysis-service' },
  'spam-intelligence': { title: 'Spam Intelligence', summary: 'Spam rates, rule hits and false-positive feedback.', backend: 'spam-intelligence-service' },
  quarantine: { title: 'Quarantine', summary: 'Review, release or delete quarantined messages.', backend: 'quarantine-service' },
  reports: { title: 'Reports', summary: 'Scheduled and on-demand mail and security reports.', backend: 'reporting-service' },
  policies: { title: 'Policy Engine', summary: 'Author and simulate mail-flow and security policies.', backend: 'policy-service' },
  'shared-mailboxes': { title: 'Shared Mailboxes', summary: 'Mailboxes shared by a team with delegated access.', backend: 'mail-admin shared-mailbox API (gateway route + facade)', planned: true },
  'distribution-lists': { title: 'Distribution Lists', summary: 'Group addresses that deliver to many recipients.', backend: 'distribution-list API', planned: true },
} satisfies Record<string, UnavailableFeature>;

export type UnavailableFeatureKey = keyof typeof UNAVAILABLE_FEATURES;

export const isFeatureEnabled = (key: string): boolean => !(key in UNAVAILABLE_FEATURES);

/** A navigation entry for this key may be shown: either available, or unavailable but planned ("Soon"). */
export const isFeatureListed = (key: string): boolean =>
  isFeatureEnabled(key) || (UNAVAILABLE_FEATURES as Record<string, UnavailableFeature>)[key]?.planned === true;
